import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import SummaryApi from '../common';

const EmpTeamLeadHistory = () => {
  const { studentId: encodedStudentId } = useParams(); // Get the encoded studentId from URL
  const navigate = useNavigate();
  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [studentName, setStudentName] = useState('');

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage] = useState(10); // 10 records per page for history

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        setLoading(true);
        setError(null);

        const decodedEmployeeId = atob(sessionStorage.getItem('H7Z4L9VD2XK5JW8QRYT6'));
        if (!decodedEmployeeId) {
          throw new Error('Employee ID not found in session storage. Please log in again.');
        }

        if (!encodedStudentId) {
          throw new Error('Encoded Student ID not found in URL parameters.');
        }

        const studentId = atob(encodedStudentId); // Decode the studentId

        const url = SummaryApi.getTlFollowupHistorByEmpIdStudId.url;
        const method = SummaryApi.getTlFollowupHistorByEmpIdStudId.method;

        const headers = {
          'Authorization': 'Bearer sandeep_university_916',
          'Content-Type': 'application/json',
        };

        const response = await fetch(url, {
          method: method,
          headers,
          body: JSON.stringify({
            employee_id: decodedEmployeeId, // Use decoded employee ID
            student_id: studentId,         // Use decoded student ID
          }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        if (result.status) {
          setHistoryData(result.data);
          if (result.data.length > 0 && result.data[0].student_name) {
            setStudentName(result.data[0].student_name);
          } else {
            setStudentName('Unknown Student');
          }
        } else {
          setError('Failed to fetch history: ' + (result.message || 'Unknown error'));
          setStudentName('Unknown Student');
        }
      } catch (e) {
        console.error("Error fetching Team Lead Follow-up History:", e);
        setError(e.message);
        setStudentName('Unknown Student');
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, [encodedStudentId]); // Depend on encodedStudentId to re-fetch when URL changes

  // Pagination Logic
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = historyData.slice(indexOfFirstRecord, indexOfLastRecord);
  const totalPages = Math.ceil(historyData.length / recordsPerPage);

  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="text-xl font-semibold text-gray-700">Loading Follow-up History...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-red-100 text-red-700 p-4">
        <div className="text-xl font-semibold mb-4">Error: {error}</div>
        <button
          onClick={() => navigate(-1)}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 shadow-md"
        >
          Go Back
        </button>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 bg-gray-50 min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800 text-center">
          Follow-up History for {studentName ? studentName : `Student ID: ${atob(encodedStudentId)}`}
        </h1>
        <button
          onClick={() => navigate(-1)}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 shadow-md"
        >
          Back to Follow-ups
        </button>
      </div>

      {historyData.length === 0 ? (
        <div className="text-center text-gray-600 text-lg py-8">No follow-up history available for this student.</div>
      ) : (
        <>
          <div className="overflow-x-auto shadow-lg rounded-lg mb-4">
            <table className="min-w-full bg-white divide-y divide-gray-200">
              <thead className="bg-gray-200">
                <tr>
                  {['S.no', 'Follow up Date', 'Next Follow-up', 'Call Status', 'Call Reason', 'Description', 'Team Lead']
                    .map((header, idx) => (
                      <th key={idx} scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                        {header}
                      </th>
                    ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentRecords.map((record, index) => ( // Use currentRecords for rendering
                  <tr key={record.tl_student_followup_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {indexOfFirstRecord + index + 1}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {new Date(record.followup_created_date).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {record.followup_next_followup_date_time ? new Date(record.followup_next_followup_date_time).toLocaleString() : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{record.call_status}</td>
                    <td className="px-6 py-4 text-sm text-gray-700 max-w-[200px] truncate hover:whitespace-normal" title={record.call_reason}>
                      {record.call_reason}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700 max-w-[200px] truncate hover:whitespace-normal" title={record.followup_lead_description}>
                      {record.followup_lead_description}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{record.team_lead_name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-4 mt-6">
              <button
                onClick={prevPage}
                disabled={currentPage === 1}
                className={`px-4 py-2 rounded-md transition-colors duration-200
                  ${currentPage === 1
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700 shadow-md'
                  }`}
              >
                Previous
              </button>
              <span className="text-lg font-semibold text-gray-700">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={nextPage}
                disabled={currentPage === totalPages}
                className={`px-4 py-2 rounded-md transition-colors duration-200
                  ${currentPage === totalPages
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700 shadow-md'
                  }`}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default EmpTeamLeadHistory;